import type { FailedToParseImportModel, ImportModel, SerialisedImportModel } from '@nx/api';
import { APP_SCOPE } from '@nx/constants';
import * as ImportShared from '@nx/import-shared';
import { createLogger } from '@nx/logger/legacy';
import type { RootState } from '@nx/state';
import { selectAuraConfiguration } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import type { BaseQueryFn, QueryReturnValue } from '@reduxjs/toolkit/query/react';

import { APP_VERSION } from '../../constants';
import { dataModelJsonFormatter, isDataModelJsonStruct } from '../../data-model/data-model-json-formatter';
import { emptyDataModel } from '../../data-model/data-model.json.helpers';
import type { AnyDeprecatedDataModel } from '../../data-model/deprecated/types';
import { isLatestDataModelVersion, migrateDataModelToLatestVersion } from '../../data-model/migrations';
import {
  createImportApiError,
  getAllFieldsInDefinition,
  getFieldWithCorrectType,
  getImportApiErrorMessage,
  isFailedToParseImportModel,
} from '../../utils/data-sources-utils';
import { generateVisualisationFromNodesAndRelationships } from '../../utils/import-model-utils';
import { getFullVisualisationModelFromVisualisationState, hydrateVisualisationState } from '../../visualisation/utils';
import type { ImportJobWithAuraCredParams, ImportJobWithAuraIdParams, UnionTypeForImportJobParams } from './types';

const logger = createLogger(APP_SCOPE.import);

export const getImportUrls = (state: RootState) => {
  let rootEndpoint = selectAuraConfiguration(state)?.importApiUrl;

  if (rootEndpoint === undefined) {
    logger.error('Import API URL not defined');
  }

  rootEndpoint = (rootEndpoint ?? '').replace(/\/$/, '');

  return {
    rootEndpoint,
  };
};

export const sortByTimeDesc = (a: string, b: string) => {
  const aTime = Date.parse(a);
  const bTime = Date.parse(b);
  if (isNaN(aTime) && isNaN(bTime)) {
    return 0;
  } else if (isNaN(aTime)) {
    return -1;
  } else if (isNaN(bTime)) {
    return 1;
  }
  return bTime - aTime;
};

// TODO: should create a /migration folder to consolidate all migration functions.
export const extractImportModelFromSerialisation = (
  serialisedImportModel: SerialisedImportModel,
): ImportModel | FailedToParseImportModel => {
  try {
    const { dataModel: serialisedDataModel, visualisation: serialisedVisualisation } = serialisedImportModel;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const dataModel: AnyDeprecatedDataModel | ImportShared.DataModelJsonStruct = JSON.parse(serialisedDataModel);
    const dataModelVersion = 'version' in dataModel ? dataModel.version : '0.0.1';
    let modelToLoad = emptyDataModel;
    if (isDataModelJsonStruct(dataModel) && isLatestDataModelVersion(dataModelVersion)) {
      modelToLoad = dataModel;
    }
    // Loaded model is an old version, migrate it to the latest version and clean up before loading it
    else {
      modelToLoad = migrateDataModelToLatestVersion(dataModel, dataModelVersion);
    }

    // Verify the data model is valid. If not, this function will throw an error for later catch.
    dataModelJsonFormatter.fromJsonStruct(modelToLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const visualisation: ImportShared.SerialisableVisualisationState = JSON.parse(serialisedVisualisation);

    return {
      ...serialisedImportModel,
      dataModel: modelToLoad,
      visualisation,
      // version is now migrated to latest version
      version: APP_VERSION,
    };
  } catch (e) {
    logger.error('Error import model to load', e);
    return {
      ...serialisedImportModel,
      dataModel: null,
      visualisation: null,
    };
  }
};

export const mapImportModelToImportModelMetadata = (
  importModel: ImportModel | FailedToParseImportModel,
): ImportShared.ImportModelMetadata | FailedToParseImportModel => {
  if (isFailedToParseImportModel(importModel)) {
    return importModel;
  }
  const { dataModel, visualisation } = importModel;

  const hydratedDataModel = dataModelJsonFormatter.fromJsonStruct(dataModel);
  const hydratedVisualisation = hydrateVisualisationState(visualisation, dataModel);

  const fullGraphVisualisationModel = getFullVisualisationModelFromVisualisationState(
    hydratedVisualisation,
    hydratedDataModel,
  );

  return {
    id: importModel.id,
    name: importModel.name ?? 'Untitled model',
    fullGraphVisualisationModel,
  };
};

const isApiCandidateGraphResponse = (response: unknown): response is ImportShared.ApiCandidateGraphResponse => {
  return isDataModelJsonStruct(response);
};

const mapFromApiTableSchemaToTableSchemaJsonStruct =
  (isLocalDataSource: boolean) =>
  (tableSchema: ImportShared.ApiTableSchema): ImportShared.TableSchemaJsonStruct => ({
    name: tableSchema.name,
    fields: tableSchema.fields.map((field) => {
      if (isLocalDataSource) {
        const localField: ImportShared.TableSchemaLocalFieldJsonStruct = {
          name: field.name,
          sample: '',
          recommendedType: field.recommendedType ?? { type: 'string' },
        };
        return localField;
      }
      const cloudField: ImportShared.TableSchemaCloudFieldJsonStruct = {
        name: field.name,
        rawType: field.rawType,
        recommendedType: field.recommendedType,
        supportedTypes: field.supportedTypes,
      };
      return cloudField;
    }),
    primaryKeys: tableSchema.primaryKeys,
    foreignKeys: tableSchema.foreignKeys,
    expanded: true,
  });

export const parseApiCandidateGraphResponseToImportModel = (response: unknown) => {
  if (!isApiCandidateGraphResponse(response)) {
    throw new Error('Candidate graph has invalid data model');
  }
  const { dataSourceSchema } = response.graphMappingRepresentation;
  if (dataSourceSchema.type !== 'cloud' && dataSourceSchema.type !== 'local') {
    throw new Error('Candidate graph has empty or invalid data source location');
  }
  const tableSchemas = dataSourceSchema.tableSchemas.map(
    mapFromApiTableSchemaToTableSchemaJsonStruct(dataSourceSchema.type === 'local'),
  );

  const dataModel: ImportShared.DataModelJsonStruct = {
    ...response,
    version: APP_VERSION,
    graphMappingRepresentation: {
      ...response.graphMappingRepresentation,
      dataSourceSchema: {
        type: dataSourceSchema.type,
        tableSchemas,
      },
    },
  };

  const { nodeObjectTypes, relationshipObjectTypes } =
    dataModelJsonFormatter.fromJsonStruct(dataModel).graphSchemaRepresentation;
  const visualisation = generateVisualisationFromNodesAndRelationships(nodeObjectTypes, relationshipObjectTypes);

  return { dataModel, visualisation };
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isMeta = (meta: unknown): meta is { response: { status: number } } => {
  if (typeof meta === 'object' && meta !== null && 'response' in meta) {
    const { response } = meta;
    if (typeof response === 'object' && response !== null && 'status' in response) {
      return typeof response.status === 'number';
    }
  }
  return false;
};

const isSpawnNewSourceSchemaJobResultData = (data: unknown): data is { id: string } => {
  return typeof data === 'object' && data !== null && 'id' in data && typeof data.id === 'string';
};

const isApiDataSourceSchema = (data: unknown): data is ImportShared.ApiDataSourceSchema => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    typeof data.type === 'string' &&
    'tableSchemas' in data &&
    Array.isArray(data.tableSchemas)
  );
};

const getArrayFormFieldValueForApi = (arr: ImportShared.ArrayFieldValue): ImportShared.ApiArrayFieldValueElement[] => {
  return arr.map((element) => {
    const arrayValue: ImportShared.ApiArrayFieldValueElement = {};
    Object.entries(element).forEach(([key, fieldObj]) => {
      arrayValue[key] = { type: fieldObj.type, value: fieldObj.value };
    });
    return arrayValue;
  });
};

export const buildDynamicParamsPayloadByDataSourceConfig = (
  fields: ImportShared.Field[],
): Record<string, ImportShared.ApiDynamicField> => {
  const emptyDynamicFields: Record<string, ImportShared.ApiDynamicField> = {};
  return fields.reduce((acc, field) => {
    if (ImportShared.isArrayField(field)) {
      const newArrayValue = getArrayFormFieldValueForApi(field.value);
      acc[field.name] = { type: field.type, value: newArrayValue };
    } else {
      acc[field.name] = { type: field.type, value: field.value };
    }
    return acc;
  }, emptyDynamicFields);
};

export const buildDynamicDataSourceParamsPayloadByDataSourceConfig = (
  dataSourceConfig: ImportShared.DataSourceConfig,
): {
  type: string;
  parameters: Record<string, ImportShared.ApiDynamicField>;
} => {
  const { type, fields } = dataSourceConfig;
  const parameters = buildDynamicParamsPayloadByDataSourceConfig(fields);

  return { type, parameters };
};

export const transformSavedDataSourceConfig = (
  dataSource: ImportShared.ApiSavedDataSourceConfig,
  dataSourceDeclarations: ImportShared.DataSourceDefinition[] | undefined,
): ImportShared.SavedDataSourceConfig | undefined => {
  const dataSourceDefinition = dataSourceDeclarations?.find((m) => m.type === dataSource.type);
  if (isNullish(dataSourceDefinition)) {
    return undefined;
  }
  const fieldsDefinition = getAllFieldsInDefinition(dataSourceDefinition);
  const fieldsWithValue: ImportShared.Field[] = fieldsDefinition
    .map((fieldName) => getFieldWithCorrectType(fieldName, dataSource.parameters[fieldName.name]?.value))
    .filter((f) => !isNullish(f));
  return {
    id: dataSource.id,
    name: dataSource.name,
    type: dataSource.type,
    fields: fieldsWithValue,
    updatedAt: dataSource.updatedAt,
    createdAt: dataSource.createdAt,
    hasPassword: dataSource.hasPassword,
  };
};

export const waitForDataSourceSchemaJobCompletion = async (
  spawnNewSourceSchemaJobResult: QueryReturnValue<unknown, unknown, Record<string, unknown>>,
  fetchWithBaseQuery: (args: { url: string; method: string }) => ReturnType<BaseQueryFn>,
) => {
  const { data: spawnNewSourceSchemaJobResultData, meta: spawnNewSourceSchemaJobResultMeta } =
    spawnNewSourceSchemaJobResult;
  if (!isSpawnNewSourceSchemaJobResultData(spawnNewSourceSchemaJobResultData)) {
    const error = !isNullish(spawnNewSourceSchemaJobResult.error)
      ? spawnNewSourceSchemaJobResult.error
      : createImportApiError(
          'No data returned from spawning a new source schema job',
          spawnNewSourceSchemaJobResultMeta !== undefined &&
            'response' in spawnNewSourceSchemaJobResultMeta &&
            typeof spawnNewSourceSchemaJobResultMeta.response === 'object' &&
            spawnNewSourceSchemaJobResultMeta.response !== null &&
            'status' in spawnNewSourceSchemaJobResultMeta.response &&
            typeof spawnNewSourceSchemaJobResultMeta.response.status === 'number'
            ? spawnNewSourceSchemaJobResultMeta.response.status
            : undefined,
        );
    return {
      error: getImportApiErrorMessage(error),
    };
  }
  const { id: jobId } = spawnNewSourceSchemaJobResultData;
  let isJobCompleted = false;
  const startTime = Date.now();
  let getSourceSchemaJobResultData: unknown;
  let getSourceSchemaJobResultError: unknown;
  let getSourceSchemaJobResultMeta: { response: { status: number } } | undefined;
  while (!isJobCompleted) {
    // Timeout after 10 minutes
    if (Date.now() - startTime > 1000 * 60 * 10) {
      return {
        // 408 indicates that the server has terminated a connection due to a slow client request.
        // But in this case, it is not a client request that is slow, but the job is taking too long to complete.
        // So we should not use 408, but 504 (Gateway Timeout) instead.
        error: getImportApiErrorMessage(createImportApiError('Timeout waiting for job to complete', 504)),
      };
    }

    // eslint-disable-next-line no-await-in-loop
    const getSourceSchemaJobResult = await fetchWithBaseQuery({
      url: `/v2/sources/schema/${jobId}`,
      method: 'GET',
    });
    const { data, error, meta } = getSourceSchemaJobResult;
    if (!isMeta(meta)) {
      return { error: getImportApiErrorMessage(createImportApiError('Unable to get job status')) };
    }
    getSourceSchemaJobResultData = data;
    getSourceSchemaJobResultError = error;
    getSourceSchemaJobResultMeta = meta;

    isJobCompleted = getSourceSchemaJobResultMeta.response.status !== 204;
    // eslint-disable-next-line no-await-in-loop
    await sleep(1000);
  }
  return isApiDataSourceSchema(getSourceSchemaJobResultData)
    ? { data: getSourceSchemaJobResultData }
    : {
        error: getImportApiErrorMessage(getSourceSchemaJobResultError, getSourceSchemaJobResultMeta?.response.status),
      };
};

export const isDataSourceParametersAndAuraCredParams = (
  params: UnionTypeForImportJobParams,
): params is ImportJobWithAuraCredParams => {
  return !params.dataSourceConfig.hasPassword && 'password' in params.auraParams;
};

export const isDataSourceParametersAndAuraIdParams = (
  params: UnionTypeForImportJobParams,
): params is ImportJobWithAuraIdParams => {
  return !params.dataSourceConfig.hasPassword && !('password' in params.auraParams);
};

export const isDataSourceIdAndAuraCredParams = (
  params: UnionTypeForImportJobParams,
): params is ImportJobWithAuraCredParams => {
  return params.dataSourceConfig.hasPassword && 'password' in params.auraParams;
};
